import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Globe,
  Activity,
  Info,
  X
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useListWebhookForwardsQuery, WebhookForwardItem } from '@/client/api/whatsapp/list-webhook-forwards.query';
import { useCreateWebhookForwardMutation } from '@/client/api/whatsapp/create-webhook-forward.mutation';
import { useUpdateWebhookForwardMutation } from '@/client/api/whatsapp/update-webhook-forward.mutation';
import { useDeleteWebhookForwardMutation } from '@/client/api/whatsapp/delete-webhook-forward.mutation';
import ConfirmDeleteDialog from '@/client/components/admin/WhatsApp/ConfirmDeleteDialog';

const EVENT_TYPES = [
  { key: 'message', label: 'Message events (received, delivered, read)' },
  { key: 'status', label: 'Status events (send status updates)' },
];

const WebhookPage: React.FC = () => {
  const { data, isLoading, refetch } = useListWebhookForwardsQuery();
  const forwards = data?.data?.forwards || [];
  const createMutation = useCreateWebhookForwardMutation();
  const updateMutation = useUpdateWebhookForwardMutation();
  const deleteMutation = useDeleteWebhookForwardMutation();

  const [showModal, setShowModal] = useState(false);
  const [url, setUrl] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>(['message']);
  const [secret, setSecret] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<WebhookForwardItem | null>(null);

  const toggleEventType = (key: string) => {
    setEventTypes(prev => prev.includes(key) ? prev.filter(e => e !== key) : [...prev, key]);
  };

  const handleCreate = () => {
    if (!url.trim() || eventTypes.length === 0) return;
    createMutation.mutate(
      { url, event_types: eventTypes, secret: secret || undefined, enabled: true },
      {
        onSuccess: () => {
          toast.success('Webhook forward created');
          setShowModal(false);
          setUrl(''); setEventTypes(['message']); setSecret('');
          refetch();
        },
        onError: (error) => toast.error(error.message || 'Failed to create the webhook forward'),
      }
    );
  };

  const toggleEnabled = (fwd: WebhookForwardItem) => {
    updateMutation.mutate(
      { forward_id: fwd.forward_id, enabled: !fwd.enabled },
      { onSuccess: () => refetch(), onError: (error) => toast.error(error.message || 'Failed to update the forward') }
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { forward_id: deleteTarget.forward_id },
      { onSuccess: () => { toast.success('Webhook forward deleted'); setDeleteTarget(null); refetch(); } }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Webhook Forwards</h1>
            <p className="text-sm text-gray-500 mt-1">Relay incoming WhatsApp webhook events to additional endpoints</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Forward
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p>
                The main WhatsApp webhook (the one Meta calls directly) and its verify token are configured via
                server environment variables — see the <span className="font-medium">Settings</span> page for their
                status. Forwards below are <span className="font-medium">additional</span> destinations that also
                receive a copy of each event, useful for testing or piping events into another system.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-5 border-b border-gray-200 flex items-center gap-2">
            <Activity className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Configured Forwards</h2>
          </div>

          {isLoading && <p className="p-5 text-sm text-gray-500">Loading...</p>}

          {!isLoading && forwards.length === 0 && (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No forwards configured</h3>
              <p className="text-sm text-gray-500">Add one to relay events to another endpoint</p>
            </div>
          )}

          <div className="divide-y divide-gray-200">
            {forwards.map(fwd => (
              <div key={fwd.forward_id} className="p-5 flex items-start justify-between">
                <div>
                  <p className="text-sm font-mono text-gray-900">{fwd.url}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {fwd.event_types.map(evt => (
                      <span key={evt} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">{evt}</span>
                    ))}
                    {!fwd.enabled && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">Disabled</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Added {moment.unix(fwd.created_at).format('YYYY-MM-DD')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleEnabled(fwd)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title={fwd.enabled ? 'Disable' : 'Enable'}
                  >
                    {fwd.enabled
                      ? <ToggleRight className="w-5 h-5 text-green-500" />
                      : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                  </button>
                  <button onClick={() => setDeleteTarget(fwd)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Add Webhook Forward</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/webhook"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Types</label>
                <div className="space-y-2">
                  {EVENT_TYPES.map(evt => (
                    <label key={evt.key} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={eventTypes.includes(evt.key)}
                        onChange={() => toggleEventType(evt.key)}
                      />
                      {evt.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signing Secret (optional)</label>
                <input
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="Used to sign forwarded requests with HMAC-SHA256"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !url.trim() || eventTypes.length === 0}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Forward'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Webhook Forward"
        itemLabel={deleteTarget?.url || 'this forward'}
        onConfirm={confirmDelete}
        isPending={deleteMutation.isPending}
        errorMessage={deleteMutation.error?.message}
      />
    </div>
  );
};

export default WebhookPage;
